// src/pages/markets/board/api/boardTypes.ts
//
// Mirrors the Pydantic models in api/api_screener_board.py. These are the wire
// types AND the domain types — the board endpoint ships raw columns, so there
// is deliberately no normaliser layer that could drift from the backend.

export type MarketMode = 'single' | 'dependent' | 'independent';

export type ResolutionStatus = 'open' | 'closing_soon' | 'closed' | 'resolved';

export type SortDir = 'asc' | 'desc';

export type TagMatch = 'any' | 'all';

export type ColumnId = 'insider' | 'all_traders' | 'new' | 'active';

export type ActivityScope = 'market' | 'event';

export type EventAgg = 'tvd' | 'max';

export type MetricMode = 'absolute' | 'relative' | 'log_odds';

export type ActivityDirection = 'any' | 'up' | 'down';

export type ResolvedMode = 'exclude' | 'include' | 'only';

export interface ScreenerMarket {
    market_id: string;
    question: string | null;
    outcome_yes_label: string | null;
    outcome_no_label: string | null;
    yes_prob: number | null;
    live_prob: number | null;
    live_prob_at: string | null;
    volume: number | null;
    volume_24h: number | null;
    liquidity?: number | null;
    closed: boolean;
    resolved: boolean;
    is_open: boolean;
    open_signal_count: number;
}

export interface ScreenerEvent {
    event_id: string;
    title: string | null;
    slug: string | null;
    image: string | null;
    category: string | null;
    market_type?: string | null;
    tags: string[];
    created_at: string | null;
    start_date: string | null;
    end_date: string | null;
    // Kept as string, not the union: an unknown status from a newer backend
    // must render, not crash.
    resolution_status: string;
    volume: number;
    volume_24h: number | null;
    liquidity?: number | null;
    market_count: number;
    open_market_count: number;
    market_mode: string;
    open_signal_count: number;
    markets: ScreenerMarket[];
    primary_market_id: string | null;
    sort_value: number | null;
}

export interface TraderScore {
    address: string;
    side: string;
    group: string;
    outcome_index: number;
    cost_usd: number;
    net_exposure_usd: number;
    net_shares: number;
    max_position_invested: number;
    avg_entry_price: number | null;
    implied_probability: number | null;
    last_position_change_at: string | null;
    position_age_seconds: number | null;
    roct: number | null;
    avg_edge: number | null;
    avg_edge_money: number | null;
    avg_edge_share: number | null;
    avg_edge_abspnl: number | null;
    win_rate: number | null;
    win_rate_money: number | null;
    win_rate_shares: number | null;
    win_rate_abspnl: number | null;
    closed_count: number;
    quality_score: number | null;
}

export interface BoardScore {
    event_id: string;
    market_id: string;
    group: string;
    ts: string;
    direction: string | null;
    capital_usd_yes: number;
    capital_usd_no: number;
    n_traders_yes: number;
    n_traders_no: number;
    raw_implied: number | null;
    open_shares_yes: number | null;
    open_shares_no: number | null;
    market_prob: number | null;
    market_question: string | null;
    outcome_yes_label: string | null;
    outcome_no_label: string | null;
    agg: Record<string, number | string | null>;
}

export interface GroupIndexEntry {
    group: string;
    market_count: number;
    yes_count: number;
    no_count: number;
}

export interface EventConvictionIndex {
    event_id: string;
    score_count: number;
    markets_with_scores: number;
    latest_ts: string;
    by_group: GroupIndexEntry[];
    primary_market_by_group: Record<string, string>;
}

export interface BoardEventsResponse {
    column: string;
    group: string | null;
    sort_by: string;
    sort_dir: SortDir;
    filters: Record<string, unknown>;
    next_cursor: string | null;
    events: ScreenerEvent[];
    scores: BoardScore[];
    index: EventConvictionIndex[];
}

export interface BoardScoresResponse {
    groups: string[];
    scores: BoardScore[];
    index: EventConvictionIndex[];
}

export interface BoardRankRow {
    event_id: string;
    sort_value: number | null;
    primary_market_id: string | null;
}

export interface BoardRankResponse {
    column: string;
    group: string | null;
    sort_by: string;
    sort_dir: SortDir;
    filters: Record<string, unknown>;
    next_cursor: string | null;
    rows: BoardRankRow[];
}

export interface BoardEventsByIdsResponse {
    events: ScreenerEvent[];
}

export interface BoardTradersResponse {
    market_id: string;
    group: string;
    ts: string;
    traders: TraderScore[];
}

export interface MarketActivity {
    market_id: string;
    drift: number | null;
    jump: number | null;
    jump_at: string | null;
    volume_now: number;
    volume_prev: number;
    volume_ratio: number | null;
    trade_count: number;
    trade_count_prev: number;
    resolved: boolean;
    resolved_at: string | null;
}

export interface EventActivity {
    event_id: string;
    scope_market_id: string | null;
    market_mode: string;
    drift: number | null;
    jump: number | null;
    jump_at: string | null;
    jump_market_id: string | null;
    event_agg_tvd: number | null;
    event_agg_max: number | null;
    volume_now: number;
    volume_prev: number;
    volume_ratio: number | null;
    trade_count: number;
    trade_count_prev: number;
    resolved: boolean;
    resolved_at: string | null;
    by_market: MarketActivity[] | null;
    computed_at: string;
}

export interface BoardActivityResponse {
    scope: string;
    metric_mode: string;
    unit: string;
    windows: Record<string, string>;
    filters: Record<string, unknown>;
    activity: EventActivity[];
}

export type FilterFieldType = 'enum' | 'number' | 'tristate';

export interface FilterFieldSpec {
    key: string;
    type: FilterFieldType;
    section?: string;
    options?: string[];
    labels?: string[];
    default?: string | number;
    min?: number;
    max?: number;
    step?: number;
    unit?: string;
    unit_by?: string;
    units?: Record<string, string>;
    defaults_by?: Record<string, number>;
    enabled_when?: Record<string, string | string[]>;
    help?: string;
}

export interface BoardColumnDefaults {
    group: string | null;
    sort_by: string;
    event_category: string[] | null;
    polymarket_tag: string[] | null;
    min_capital?: number | null;
    min_traders?: number | null;
    max_age_days?: number;
    scope?: ActivityScope;
    event_agg?: EventAgg;
    metric_mode?: MetricMode;
    drift_window?: string;
    min_drift?: number;
    jump_window?: string;
    min_jump?: number;
    min_volume_usd?: number;
    direction?: ActivityDirection;
    resolved_mode?: ResolvedMode;
}

export interface BoardSchema {
    version: string;
    groups: string[];
    event_categories: string[];
    polymarket_tags: string[];
    sort_options: Record<string, string[]>;
    column_defaults: Record<string, BoardColumnDefaults>;
    filter_spec?: Record<string, FilterFieldSpec[]>;
    activity_windows?: Record<string, string[]>;
    thresholds_by_mode?: Record<string, Record<string, number>>;
    series_windows: string[];
    series_resolutions: string[];
    series_metrics?: string[];
    // `group_info` used to live here. Group presentation is read from the
    // `board_groups` Firestore collection now, and the indexer stops shipping
    // it; the field is ignored if an older deploy still sends it.
    column_info?: Record<string, ColumnInfo>;
    section_help?: Record<string, string>;
    stats_windows?: string[];
}

export interface SeriesPoint {
    t: string;
    close: number | null;
}

export interface MarketSeries {
    market_id: string;
    points: SeriesPoint[];
}

export interface BoardSeriesResponse {
    window: string;
    resolution: string;
    series: MarketSeries[];
}

export interface BaseFilters {
    group: string | null;
    sortBy: string;
    sortDir: SortDir;
    eventsInclude: string[] | null;
    eventsExclude: string[] | null;
    tagsInclude: string[] | null;
    tagsExclude: string[] | null;
    tagMatch: TagMatch;
}

export interface ConvictionFilters {
    minCapital: number | null;
    minTraders: number | null;
    maxAgeDays: number | null;
}

export interface ActivityFilters {
    scope: ActivityScope;
    eventAgg: EventAgg;
    metricMode: MetricMode;
    driftWindow: string;
    minDrift: number | null;
    jumpWindow: string;
    minJump: number | null;
    minVolumeUsd: number | null;
    direction: ActivityDirection;
    resolvedMode: ResolvedMode;
}

// Partial on everything past the base block: one column's filter set is not
// another's, and a conviction call site must keep compiling without knowing
// that activity fields exist.
export type BoardFilters = BaseFilters & Partial<ConvictionFilters & ActivityFilters>;

export type GatedFilterKey = keyof (ConvictionFilters & ActivityFilters);

// `satisfies` rather than an annotation: the literal types survive, so
// defaults[FILTER_WIRE_KEYS[field]] is a checked lookup instead of a cast, and
// a wire key that is not a real BoardColumnDefaults field fails here. Every
// field in this table is gated by filter_spec — a column that does not declare
// the key never sends it, and the backend answers 400 if one slips through.
export const FILTER_WIRE_KEYS = {
    minCapital: 'min_capital',
    minTraders: 'min_traders',
    maxAgeDays: 'max_age_days',
    scope: 'scope',
    eventAgg: 'event_agg',
    metricMode: 'metric_mode',
    driftWindow: 'drift_window',
    minDrift: 'min_drift',
    jumpWindow: 'jump_window',
    minJump: 'min_jump',
    minVolumeUsd: 'min_volume_usd',
    direction: 'direction',
    resolvedMode: 'resolved_mode',
} as const satisfies Record<GatedFilterKey, keyof BoardColumnDefaults>;

export type FilterWireKey = (typeof FILTER_WIRE_KEYS)[GatedFilterKey];

export const GATED_FIELDS = Object.keys(FILTER_WIRE_KEYS) as GatedFilterKey[];

export const FILTER_FIELD_BY_WIRE: Record<string, GatedFilterKey> = Object.fromEntries(
    GATED_FIELDS.map(f => [FILTER_WIRE_KEYS[f], f]),
) as Record<string, GatedFilterKey>;

// GroupInfo was here. Its replacement is BoardGroup in state/boardGroups.ts,
// backed by Firestore rather than by a dict in the indexer that covered 2 of
// the 18 groups.

export interface ColumnInfo {
    column: string;
    label: string;
    one_liner: string;
    description: string;
    reads: string[];
}

export interface GroupStatsTotals {
    signal_count: number;
    event_count: number;
    changed_in_window: number;
    capital_usd: number;
    trader_positions: number;
    yes_count: number;
    no_count: number;
    median_abs_disparity: number | null;
    latest_ts: string | null;
}

export interface GroupStatsBucket {
    key: string;
    signal_count: number;
    event_count: number;
    capital_usd: number;
}

export interface GroupStatsSignal {
    event_id: string;
    market_id: string;
    title: string | null;
    question: string | null;
    category: string | null;
    direction: string | null;
    outcome_yes_label: string | null;
    outcome_no_label: string | null;
    capital_usd_yes: number;
    capital_usd_no: number;
    dominant_capital: number;
    n_traders_yes: number;
    n_traders_no: number;
    raw_implied: number | null;
    market_prob: number | null;
    ts: string;
    changed_in_window: boolean;
}

export interface BoardGroupStatsResponse {
    group: string;
    window: string;
    computed_at: string;
    filters: Record<string, unknown>;
    totals: GroupStatsTotals;
    by_category: GroupStatsBucket[];
    by_tag: GroupStatsBucket[];
    top_signals: GroupStatsSignal[];
}